import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useToast } from '@app/app/_components/Toast/useToast';
import { PhoneMiniIcon } from '@app/app/_icons/PhoneMiniIcon';
import { FirstStepSaveFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/FirstStepSaveFooter';
import { HalfwayOverviewFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/HalfwayOverviewFooter';
import { Client } from '@app/domain/model/Client';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { LIFE_INSURANCE_STEPS } from '@app/app/esales/life/_sections/steps/LIFE_INSURANCE_STEPS';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { SecondStepSaveFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/SecondStepSaveFooter';
import { SummaryFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/SummaryFooter';
import { MidFormFinishFooter } from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/LifeInsuranceWizardFooter/MidFormFinishFooter';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { TenantMetadata } from '@app/infrastructure/tenant/model/TenantMetadata';

interface Props {
    currentStepKey: string;
    formState: FormState<PurchaseLifeInsuranceResult>;
    orientationalPrice: number;
    callNumber: string;
    clients: Client[];
    onAuthSuccess?: () => void;
    handleSubmit: () => void;
    handleFinishAfterHealthQuestions: () => void;
    authState: AuthState;
    tenant: TenantMetadata;
}

export function LifeInsuranceWizardFooter({
    currentStepKey,
    formState,
    orientationalPrice,
    callNumber,
    clients,
    onAuthSuccess,
    handleSubmit,
    handleFinishAfterHealthQuestions,
    authState,
    tenant,
}: Props) {
    const t = useTranslations();
    const { addToast } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const displayPrice = useMemo(() => {
        if (formState.data?.offer?.premium != null) {
            return formState.data.offer.premium;
        }

        if (
            (currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CHOOSE_PACKAGE ||
                currentStepKey === LIFE_INSURANCE_STEPS.START ||
                currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.MAIN_REASON ||
                currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.AGE ||
                currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.INCOME) &&
            formState.data?.formData.selectedPackage === LifePackageCode.Free
        ) {
            return orientationalPrice;
        }

        if (formState.data?.calculation?.premium) {
            return formState.data.calculation.premium;
        }

        return orientationalPrice;
    }, [formState.data, orientationalPrice, currentStepKey]);

    const handleSuccess = useCallback(() => {
        addToast('success', t('eshop.life.savedSuccessfully'));

        if (
            currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY ||
            currentStepKey === LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY
        ) {
            setIsModalOpen(false);
            handleFinishAfterHealthQuestions();
        } else {
            setIsModalOpen(false);
        }
    }, [addToast, currentStepKey, handleFinishAfterHealthQuestions, t]);

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const priceLabel = useMemo(() => {
        if (formState.data?.offer?.premium != null) {
            return t('eshop.life.toPayNow');
        }

        if (formState.data?.calculation?.premium) {
            return t('eshop.life.monthlyPayment');
        }

        return t('eshop.life.monthlyPaymentFrom');
    }, [formState.data?.calculation?.premium, formState.data?.offer?.premium, t]);

    if (currentStepKey === LIFE_INSURANCE_STEPS.PACKAGES_DONE) {
        return (
            <FirstStepSaveFooter
                currentStepKey={currentStepKey}
                formState={formState}
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                handleSuccess={handleSuccess}
                openModal={openModal}
            />
        );
    }

    if (currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN) {
        return (
            <HalfwayOverviewFooter
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                handleSaveSuccess={handleSuccess}
                handleSubmit={handleSubmit}
                formState={formState}
                openModal={openModal}
                authState={authState}
                clients={clients}
                onAuthSuccess={onAuthSuccess}
                tenant={tenant}
            />
        );
    }

    if (currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS_SUMMARY) {
        return (
            <MidFormFinishFooter
                authState={authState}
                formState={formState}
                handleSuccess={handleSuccess}
                currentStepKey={currentStepKey}
            />
        );
    }

    if (currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH_DONE) {
        return <SecondStepSaveFooter authState={authState} formState={formState} handleSuccess={handleSuccess} />;
    }

    if (currentStepKey === LIFE_INSURANCE_STEPS.SUMMARY) {
        return <SummaryFooter authState={authState} formState={formState} handleSuccess={handleSuccess} />;
    }

    if (currentStepKey === LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION_SUMMARY) {
        return (
            <MidFormFinishFooter
                authState={authState}
                formState={formState}
                handleSuccess={handleSuccess}
                currentStepKey={currentStepKey}
            />
        );
    }

    return (
        <div className="flex w-fit flex-row items-center sm:gap-4">
            {currentStepKey !== LIFE_INSURANCE_STEPS.START && (
                <div className="flex flex-col pr-3 sm:pr-0">
                    <Paragraph size="small">{priceLabel}</Paragraph>
                    <Heading level="h2">{formatEuroValue(displayPrice, tenant.tenant)}</Heading>
                </div>
            )}
            <Button
                preventDefault
                variant="outlined"
                className="max-h-16 min-h-16 min-w-16 max-w-16 rounded-full py-5"
                size="small"
                href={`tel:${callNumber}`}
                useAnchorTag>
                <PhoneMiniIcon className="size-6" />
            </Button>
        </div>
    );
}
